import { getInvitationsState } from '@domains/protected/invitations/stores/invitations.store';
import { learningCenterStore } from '@domains/protected/learning-center/stores/learningCenter';
import {
  responsesStore,
  responsesStoreActions
} from '@domains/protected/responses/stores/responses';
import { getWorkspaceState } from '@domains/protected/workspace/stores/workspace';
import { requestService } from '@services/request';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { globalUserGroupsStore } from '@stores/bootstrap-stores/userGroups';
import { Module } from '@vincerosoft/assessfluence-types';

export type OfflineModule = Extract<
  Module,
  'invitations' | 'workspace' | 'learning-center' | 'responses'
>;

export class OfflineService {
  private syncPromise: Promise<void> | null = null;

  echo() {
    return requestService<{
      success: boolean;
      message: string;
    }>(`/echo`, {
      method: 'GET',
      skipBearerToken: true
    });
  }

  async sync(modulesToSync: OfflineModule[]) {
    if (!modulesToSync.length) return;

    if (this.syncPromise) {
      return this.syncPromise;
    }

    this.syncPromise = this.syncModules(modulesToSync).finally(() => {
      this.syncPromise = null;
    });

    return this.syncPromise;
  }

  private async syncModules(modulesToSync: OfflineModule[]) {
    if (modulesToSync.includes('invitations')) {
      await this.syncInvitations();
    }

    if (modulesToSync.includes('responses')) {
      await this.syncResponses();
    }

    if (modulesToSync.includes('workspace')) {
      await this.syncWorkspace();
    }

    if (modulesToSync.includes('learning-center')) {
      await this.syncLearningCenter();
    }
  }

  private async syncInvitations() {
    const member = appUserStore.getState().currentMember;
    if (!member) {
      throw new Error('Cannot sync invitations before the member context is initialized');
    }

    const userGroups = globalUserGroupsStore.getState().userGroups || [];
    const invitationsState = getInvitationsState();

    await invitationsState.initInvitations(member, userGroups);
    const invitationIds = getInvitationsState().invitations.map((invitation) => invitation.id);
    await getInvitationsState().getResponses(invitationIds);
  }

  private async syncResponses() {
    const responsesState = responsesStoreActions();

    await responsesState.initResponsesStore();
    const responses = responsesStore.getState().responses || [];
    const responseIds = responses.map((response) => response.id);

    await responsesStoreActions().getInvitations(responseIds);
    await responsesStoreActions().getAssociatedData(responses);
  }

  private async syncWorkspace() {
    const workspaceState = getWorkspaceState();

    await workspaceState.initWorkspace();
    const forms = Object.values(getWorkspaceState().byFormId).map((entry) => entry.form);

    if (forms.length) {
      await getWorkspaceState().getTaxonomies(forms);
    }
  }

  private async syncLearningCenter() {
    await learningCenterStore.getState().initLearningCenter();
    const formGroups = Object.values(learningCenterStore.getState().byFormGroupId).map(
      (entry) => entry.formGroup
    );

    if (formGroups.length) {
      await learningCenterStore.getState().getTaxonomyData(formGroups);
    }
  }
}

export const offlineService = new OfflineService();
